import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import SelectPintoCategory from '../../../components/modal/SelectPintoCategory';
import { ContentType, PintoCategory } from '../../../core/graphql/types';
import useQueryPintoCategories from '../../../hooks/useQueryPintoCategories';
import { findCategoryById } from '../../../utils/categoryUtils';

interface IPropsSelectPintoCategory {
  name: string;
  categoryId?: number;
  /** e.g. "configItems.2.contentType", or undefined when used for target */
  contentTypeName?: string;
  allowSelectAll?: boolean;
  disableSelectAll?: boolean;
  selectAllDisabledMessage?: string;
}

const SelectPintoCategoryWrapper = (props: IPropsSelectPintoCategory) => {
  const { setValue } = useFormContext();
  const { pintoCategories: allCategories } = useQueryPintoCategories();

  const { categoryId, name, contentTypeName, allowSelectAll = true, disableSelectAll = false, selectAllDisabledMessage } = props;
  const [categoryName, setCategoryName] = useState<string>('');

  const onSetPintoCategory = useCallback(
    (categories: PintoCategory[]) => {
      const selectedCategory = categories?.[(categories?.length || 0) - 1];
      const selectedCategoryId = selectedCategory?._id;
      const selectedContentType = selectedCategory?.contentType;
      const selectedCategoryName = selectedCategory?.name;
      const isSelectAll = selectedCategoryId === '0';

      setValue?.(name, isSelectAll ? 0 : selectedCategoryId);

      if (contentTypeName) {
        setValue(contentTypeName, isSelectAll ? undefined : selectedContentType);
        if (isSelectAll || selectedContentType !== ContentType.Comic) {
          const anyWritingPath = contentTypeName.replace(/\.contentType$/, '.anyWritingTypes');
          setValue(anyWritingPath, []);
        }
      } else {
        setValue?.('contentType', isSelectAll ? undefined : selectedContentType);
        if (selectedContentType !== ContentType.Comic) {
          setValue('target.anyWritingTypes', []);
        }
      }

      setCategoryName(selectedCategoryName || ''); // Update category name
    },
    [contentTypeName, name, setValue],
  );

  useEffect(() => {
    if (categoryId === 0) {
      setCategoryName('เลือกทั้งหมด');
      return;
    }

    if (allCategories?.pintoCategories && categoryId) {
      const categoryHierarchy = findCategoryById(allCategories.pintoCategories.filter((category) => category !== null) as PintoCategory[], categoryId);
      if (categoryHierarchy) {
        setCategoryName(categoryHierarchy[categoryHierarchy.length - 1]?.name || '');
      }
      return;
    }

    setCategoryName('');
  }, [allCategories, categoryId]);

  return (
    <div>
      <label className="label">
        <span className="label-text text-systemGrays02LabelSecondary">
          Category <span className="text-colorsBrandRed01Primary">*</span>
        </span>
      </label>
      <div className="space-x-16">
        <div
          className={classNames('py-16 badge', {
            'text-colorsBrandRed01Primary bg-transparent border-colorsBrandRed01Primary': !!categoryId || categoryId === 0,
            'badge-outline': !categoryId && categoryId !== 0,
          })}
        >
          {`CategoryId: ${categoryId === 0 ? 'ALL' : categoryId || 'N/A'}`} , {`Name: ${categoryName || 'N/A'}`}
        </div>
        <SelectPintoCategory
          openModalLabel={categoryId || categoryId === 0 ? 'change Category' : 'select category'}
          onConfirm={onSetPintoCategory}
          name={name}
          allCategories={(allCategories?.pintoCategories ?? []).filter((category) => category !== null) as PintoCategory[]}
          allowSelectAll={allowSelectAll}
          disableSelectAll={disableSelectAll}
          selectAllDisabledMessage={selectAllDisabledMessage}
          isSelectAllSelected={categoryId === 0}
        />
      </div>
    </div>
  );
};

export default SelectPintoCategoryWrapper;
